"use client";

import { VisitorTypeField } from "@gettactic/api/src/endpoints/visitor-manager";
import { CrudModal } from "@gettactic/components/src/overlay/modals/CrudModal";
import { getApiErrorMessage } from "@gettactic/helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { api } from "@/lib/api/api";
import { RegistrationCodeModal } from "./RegistrationCodeModal";
import {
  buildVisitCreatePayload,
  PRE_REGISTER_DEFAULT_VALUES,
  type PreRegisterFormValues,
  preRegisterSchema,
  VisitorPreRegistrationFormFields
} from "./VisitorPreRegistrationFormFields";

interface PreRegisterVisitorModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PreRegisterVisitorModal: React.FC<
  PreRegisterVisitorModalProps
> = ({ isOpen, onOpenChange }) => {
  const queryClient = useQueryClient();
  const [registrationCode, setRegistrationCode] = useState<string | null>(null);
  const customFieldsRef = useRef<VisitorTypeField[]>([]);

  const form = useForm<PreRegisterFormValues>({
    resolver: zodResolver(preRegisterSchema),
    defaultValues: PRE_REGISTER_DEFAULT_VALUES
  });

  // Subscribe to formState.errors so child inputs re-render on validation failure
  const { errors: _formErrors } = form.formState;

  const createMutation = useMutation({
    mutationFn: async (data: PreRegisterFormValues) => {
      const payload = buildVisitCreatePayload(data, customFieldsRef.current);
      const response = await api.client.visits.create(payload);
      return response.result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["visits.list"]
      });
      onOpenChange(false);
      const code = result?.code;
      if (code) {
        setRegistrationCode(code);
      } else {
        toast.success("Visitor pre-registered successfully");
      }
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Failed to pre-register visitor"));
    }
  });

  const handleSubmit = useCallback(
    () =>
      form.handleSubmit(async (data) => {
        await createMutation.mutateAsync(data);
      })(),
    [form, createMutation]
  );

  return (
    <>
      <CrudModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        title="Pre-Register Visitor"
        isEditing={false}
        form={form}
        onSubmit={handleSubmit}
        submitLabel="Pre-Register"
        defaultValues={PRE_REGISTER_DEFAULT_VALUES}
        unmountContent
      >
        <VisitorPreRegistrationFormFields
          form={form}
          isActive={isOpen}
          customFieldsRef={customFieldsRef}
        />
      </CrudModal>

      <RegistrationCodeModal
        code={registrationCode}
        onClose={() => setRegistrationCode(null)}
      />
    </>
  );
};
